import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../../utils/axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { StatCard } from './components/StatCard';
import { SectionStatusChip } from './components/SectionStatusChip';
import { ViewControls } from './components/ViewControls';
import { KpiTimeChart } from './components/KpiTimeChart';
import { fmtNum, fmtNumShort, fmtPeriodo, calcDelta, prevPeriodo } from './components/kpiUtils';
import { TrendingUp, Smartphone, Apple, Upload, ClipboardPaste, Download } from 'lucide-react';
import { GlosarioHelpButton } from './components/GlosarioHelpButton';
import { SectionExportButton } from './components/SectionExportButton';

const IOS = '#6366f1';
const AND = '#10b981';

const KpisAdquisicion = () => {
  const [data, setData] = useState({ series: [], por_mes: [], totales: {}, puede_cargar: false });
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('evolucion');
  const [chartType, setChartType] = useState('area');
  const [gran, setGran] = useState('mensual');
  const [mesUno, setMesUno] = useState('');
  const [mesA, setMesA] = useState('');
  const [mesB, setMesB] = useState('');

  const [plataforma, setPlataforma] = useState('ios');
  const [texto, setTexto] = useState('');
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);
  const rootRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get('/kpis/descargas');
      setData(r.data);
      const ps = (r.data.por_mes || []).map((m) => m.periodo);
      if (ps.length) {
        setMesUno((p) => p || ps[ps.length - 1]);
        setMesB((p) => p || ps[ps.length - 1]);
        setMesA((p) => p || ps[ps.length - 2] || ps[ps.length - 1]);
      }
    } catch (e) { toast.error('Error al cargar descargas'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const { series, por_mes, totales, puede_cargar } = data;
  const periodos = useMemo(() => (por_mes || []).map((m) => m.periodo), [por_mes]);
  const mesMap = useMemo(() => Object.fromEntries((por_mes || []).map((m) => [m.periodo, m])), [por_mes]);

  const importarTexto = async () => {
    if (!texto.trim()) { toast.error('Pegá el texto exportado de la tienda'); return; }
    setImporting(true);
    try {
      const r = await api.post('/kpis/descargas/importar', { plataforma, texto });
      toast.success(`${plataforma === 'ios' ? 'iOS' : 'Android'}: ${r.data.insertados} nuevos, ${r.data.actualizados} actualizados`);
      setTexto(''); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo importar el texto'); }
    finally { setImporting(false); }
  };
  const importarArchivo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append('plataforma', plataforma); fd.append('file', file);
      const r = await api.post('/kpis/descargas/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${plataforma === 'ios' ? 'iOS' : 'Android'}: ${r.data.insertados} nuevos, ${r.data.actualizados} actualizados`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'No se pudo importar el archivo'); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  // datasets
  const evoRows = useMemo(() => {
    if (gran === 'diario') return (series || []).map((s) => ({ label: s.fecha.slice(5), ios: s.ios, android: s.android, total: s.total }));
    return (por_mes || []).map((m) => ({ label: fmtPeriodo(m.periodo, true), ios: m.ios, android: m.android, total: m.total }));
  }, [series, por_mes, gran]);
  const evoSeries = chartType === 'linea'
    ? [{ key: 'total', name: 'Total', color: '#0f172a', strong: true }, { key: 'ios', name: 'iOS', color: IOS }, { key: 'android', name: 'Android', color: AND }]
    : [{ key: 'ios', name: 'iOS', color: IOS }, { key: 'android', name: 'Android', color: AND }];

  const un = mesMap[mesUno] || { ios: 0, android: 0, total: 0 };
  const unPrev = mesMap[prevPeriodo(mesUno)] || {};
  const unRows = [{ label: 'iOS', v: un.ios, _c: IOS }, { label: 'Android', v: un.android, _c: AND }];

  const a = mesMap[mesA] || { ios: 0, android: 0, total: 0 };
  const b = mesMap[mesB] || { ios: 0, android: 0, total: 0 };
  const cmpRows = [
    { label: 'iOS', A: a.ios, B: b.ios },
    { label: 'Android', A: a.android, B: b.android },
    { label: 'Total', A: a.total, B: b.total },
  ];

  if (loading) return <div className="p-8 text-slate-500" data-testid="kpis-adquisicion-loading">Cargando descargas…</div>;

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" ref={rootRef} data-testid="kpis-adquisicion">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-sky-500/10"><TrendingUp className="h-6 w-6 text-sky-600" /></div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Descargas</h1>
            <div className="mt-1.5"><SectionStatusChip seccionKey="kpis-adquisicion" /></div>
            <p className="text-sm text-slate-500">Descargas de la app en iOS y Android</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <GlosarioHelpButton seccion="descargas" testId="descargas-help-btn" />
          <SectionExportButton title="Descargas" targetRef={rootRef} testId="descargas-export-btn" />
        </div>
      </div>

      {/* KPIs por vista */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {view === 'comparar' ? (
          <>
            <StatCard label={`Total ${fmtPeriodo(mesA)}`} value={fmtNum(a.total)} icon={Download} accent="#4f46e5" testId="descargas-kpi-a" />
            <StatCard label={`Total ${fmtPeriodo(mesB)}`} value={fmtNum(b.total)} icon={Download} accent="#0ea5e9" testId="descargas-kpi-b" />
            <StatCard label="Variación" value={calcDelta(b.total, a.total) ? `${calcDelta(b.total, a.total).pct >= 0 ? '+' : ''}${calcDelta(b.total, a.total).pct.toFixed(1)}%` : '—'} icon={TrendingUp} accent="#10b981" delta={calcDelta(b.total, a.total)} deltaLabel={`${fmtPeriodo(mesA)} → ${fmtPeriodo(mesB)}`} testId="descargas-kpi-var" />
          </>
        ) : view === 'un-mes' ? (
          <>
            <StatCard label={`Total ${fmtPeriodo(mesUno)}`} value={fmtNum(un.total)} icon={Download} accent="#4f46e5" delta={calcDelta(un.total, unPrev.total)} deltaLabel="vs mes anterior" testId="descargas-kpi-total" />
            <StatCard label="iOS" value={fmtNum(un.ios)} icon={Apple} accent={IOS} delta={calcDelta(un.ios, unPrev.ios)} testId="descargas-kpi-ios" />
            <StatCard label="Android" value={fmtNum(un.android)} icon={Smartphone} accent={AND} delta={calcDelta(un.android, unPrev.android)} testId="descargas-kpi-android" />
          </>
        ) : (
          <>
            <StatCard label="Descargas totales" value={fmtNum(totales?.total)} icon={Download} accent="#4f46e5" testId="descargas-kpi-total" />
            <StatCard label="iOS" value={fmtNum(totales?.ios)} icon={Apple} accent={IOS} testId="descargas-kpi-ios" />
            <StatCard label="Android" value={fmtNum(totales?.android)} icon={Smartphone} accent={AND} testId="descargas-kpi-android" />
          </>
        )}
      </div>

      {/* Import panel (admin) */}
      {puede_cargar && (
        <Card className="p-5 mb-6 border-dashed border-2 border-sky-200 dark:border-sky-900/60 bg-sky-50/40 dark:bg-sky-950/20" data-testid="descargas-import-panel">
          <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200 mb-3 flex items-center gap-2"><Upload className="h-4 w-4" /> Cargar descargas</h2>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
            <div className="w-full sm:w-44">
              <Label>Plataforma</Label>
              <Select value={plataforma} onValueChange={setPlataforma}>
                <SelectTrigger data-testid="descargas-plataforma-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ios">iOS (App Store)</SelectItem>
                  <SelectItem value="android">Android (Google Play)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Label>Pegar texto exportado</Label>
              <Textarea value={texto} onChange={(e) => setTexto(e.target.value)} rows={3}
                placeholder={plataforma === 'ios' ? '14 Jul 2026\t1\n13 Jul 2026\t6 ...' : '11 jul 2026\nPorcentaje del total\n19\n100 %\n...'}
                className="font-mono text-xs bg-white dark:bg-slate-900" data-testid="descargas-textarea" />
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            <Button onClick={importarTexto} disabled={importing} className="bg-sky-600 hover:bg-sky-700 active:scale-95 transition-transform" data-testid="descargas-importar-texto-btn"><ClipboardPaste className="h-4 w-4 mr-1" /> Importar texto</Button>
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="descargas-subir-archivo-btn"><Upload className="h-4 w-4 mr-1" /> Subir archivo (.txt)</Button>
            <input ref={fileRef} type="file" accept=".txt,.csv,text/plain" className="hidden" onChange={importarArchivo} data-testid="descargas-file-input" />
          </div>
          <p className="text-xs text-slate-400 mt-2">Se deduplica por fecha + plataforma: reimportar el mismo día actualiza el valor, no duplica.</p>
        </Card>
      )}

      {/* Chart card */}
      <Card className="p-5 lg:p-6" data-testid="descargas-chart-card">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-5">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold tracking-tight text-slate-800 dark:text-slate-200">
              {view === 'comparar' ? 'Comparación por plataforma' : view === 'un-mes' ? `Composición · ${fmtPeriodo(mesUno)}` : 'Evolución de descargas'}
            </h2>
            {view === 'evolucion' && (
              <div className="inline-flex rounded-lg border border-slate-200 dark:border-slate-700 p-0.5 bg-slate-50 dark:bg-slate-800 text-xs">
                <button onClick={() => setGran('mensual')} className={`px-2.5 py-1 rounded-md transition-colors ${gran === 'mensual' ? 'bg-white dark:bg-slate-900 shadow-sm text-indigo-600' : 'text-slate-400'}`} data-testid="descargas-gran-mensual">Mensual</button>
                <button onClick={() => setGran('diario')} className={`px-2.5 py-1 rounded-md transition-colors ${gran === 'diario' ? 'bg-white dark:bg-slate-900 shadow-sm text-indigo-600' : 'text-slate-400'}`} data-testid="descargas-gran-diario">Diario</button>
              </div>
            )}
          </div>
          <ViewControls view={view} onView={setView} chartType={chartType} onChartType={setChartType}
            periodos={periodos} mesUno={mesUno} onMesUno={setMesUno} mesA={mesA} onMesA={setMesA} mesB={mesB} onMesB={setMesB}
            showChartType={view === 'evolucion'} />
        </div>

        {view === 'evolucion' && (
          <KpiTimeChart data={evoRows} series={evoSeries} type={chartType} stacked={chartType !== 'linea'}
            valueFormatter={fmtNum} axisFormatter={fmtNumShort} testId="descargas-chart" />
        )}
        {view === 'un-mes' && (
          <KpiTimeChart data={unRows} xKey="label" series={[{ key: 'v', name: 'Descargas', color: IOS }]} type="barras"
            cellColors={[IOS, AND]} valueFormatter={fmtNum} axisFormatter={fmtNumShort} showLegend={false} height={280} testId="descargas-chart-mes" />
        )}
        {view === 'comparar' && (
          <KpiTimeChart data={cmpRows} xKey="label"
            series={[{ key: 'A', name: fmtPeriodo(mesA), color: '#4f46e5' }, { key: 'B', name: fmtPeriodo(mesB), color: '#0ea5e9' }]}
            type="barras" valueFormatter={fmtNum} axisFormatter={fmtNumShort} height={320} testId="descargas-chart-comparar" />
        )}
      </Card>

      {/* Vista tabla: detalle mensual de descargas */}
      <Card className="p-0 overflow-hidden mt-6" data-testid="descargas-tabla">
        <div className="p-5 border-b border-slate-100 dark:border-slate-800">
          <h2 className="text-base font-semibold tracking-tight text-slate-800 dark:text-slate-200">Detalle mensual (tabla)</h2>
          <p className="text-xs text-slate-400 mt-1">Descargas por mes y plataforma.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-800 text-slate-500">
                <th className="text-left font-medium px-4 py-2.5">Mes</th>
                <th className="text-right font-medium px-4 py-2.5">iOS</th>
                <th className="text-right font-medium px-4 py-2.5">Android</th>
                <th className="text-right font-medium px-4 py-2.5">Total</th>
              </tr>
            </thead>
            <tbody>
              {[...(por_mes || [])].reverse().map((m) => (
                <tr key={m.periodo} className="border-t border-slate-100 dark:border-slate-800" data-testid={`descargas-row-${m.periodo}`}>
                  <td className="px-4 py-2.5 font-medium text-slate-700 dark:text-slate-200">{fmtPeriodo(m.periodo)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums" style={{ color: IOS }}>{fmtNum(m.ios)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums" style={{ color: AND }}>{fmtNum(m.android)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums font-semibold">{fmtNum(m.total)}</td>
                </tr>
              ))}
              {(por_mes || []).length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400">No hay descargas cargadas.</td></tr>}
            </tbody>
            {(por_mes || []).length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 font-semibold">
                  <td className="px-4 py-2.5">Total</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{fmtNum(totales?.ios)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{fmtNum(totales?.android)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{fmtNum(totales?.total)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Card>
    </div>
  );
};

export default KpisAdquisicion;
